import { create } from 'zustand';
import {
    ComposerAreaNode,
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributeType,
    ComposerEcommerceSettings,
    ComposerExtensionFieldDefinition,
    ComposerMerchantProductOption,
    ComposerOptionActionNode,
    ComposerPrintTypeDefinition,
    ComposerTemplateNode,
    ComposerTemplateSideRestrictions
} from '../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { composerQueriesFontQuery$data } from './__generated__/composerQueriesFontQuery.graphql';
import {
    ComposerValidationContext,
    upsertAndValidateComposerOption,
    validateComposerActions,
    validateComposerAreas,
    validateComposerAttributes,
    validateComposerOptions
} from './helpers/composerAttributeValidation';
import { createCopyName, createSecureClonedName } from './helpers/composerHelpers';
import { getComposerDefaultTemplate } from './helpers/composerDefaultTemplate';
import { ComposerAttributeNodeAdapter, ComposerTemplateNodeAdapter } from './helpers/composerNodesAdapter';
import { LazyValidateAttributes, LazyValidateOptions } from './helpers/composerValidation';

export type ComposerAttributeFace = 'options' | 'areas' | 'layout' | 'settings' | 'links' | 'retarget';
export type ComposerOptionFace = 'actions' | 'settings' | 'links' | 'retarget' | 'prices';

// attributes > options > actions
type ComposerStore = {
    model: MPlaza.Model | null;
    setModel: (model: MPlaza.Model) => void;

    //Scene specific
    sceneMaterialList: BABYLON.Material[];
    sceneObjectList: BABYLON.AbstractMesh[];
    sceneCameraList: BABYLON.Camera[];
    setSceneMaterialList: (materials: BABYLON.Material[]) => void;
    setSceneObjectList: (objects: BABYLON.AbstractMesh[]) => void;
    setSceneCameraList: (cameras: BABYLON.Camera[]) => void;

    modelAttributes: MPlaza.Attributes | null;

    selectedAttributes: ComposerAttributeNode[] | null;
    selectedOptions: ComposerAttributeOptionNode[] | null;
    selectedActions: ComposerOptionActionNode[] | null;
    attributeFace: ComposerAttributeFace;
    optionFace: ComposerOptionFace;
    setAttributeFace: (face: ComposerAttributeFace) => void;
    setOptionFace: (face: ComposerOptionFace) => void;

    attributes: ComposerAttributeNode[];
    attributesSteps: ComposerAttributeNode[] | null;
    attributesGroups: ComposerAttributeNode[] | null;

    templates: ComposerTemplateNode[];
    selectedTemplates: ComposerTemplateNode[] | null;
    areas: ComposerAreaNode[];
    selectedAreas: ComposerAreaNode[] | null;
    designs: Map<string, MPlaza.Design>; // key: stable UI template id, stable UI area id
    getDesign: (templateID: string | number, areaID: string | number) => MPlaza.Design | null;
    setDesigns: (designs: Map<string, MPlaza.Design>) => void;
    updateDesign: (templateID: string | number, areaID: string | number, design: MPlaza.Design) => void;

    //quantity settings page
    productQuantity: MPlaza.QuantityRule | null;

    ecommerceSettings: ComposerEcommerceSettings | null;

    merchantProductOptions: ComposerMerchantProductOption[];
    merchantProductOptionsError: string | null;
    printTypes: ComposerPrintTypeDefinition[];
    extensionFieldDefinitions: ComposerExtensionFieldDefinition[];
    setRequiredEditorData: (data: {
        merchantProductOptions: ComposerMerchantProductOption[];
        merchantProductOptionsError: string | null;
        printTypes: ComposerPrintTypeDefinition[];
        extensionFieldDefinitions: ComposerExtensionFieldDefinition[];
    }) => void;

    fonts: composerQueriesFontQuery$data | null;
    setFonts: (fonts: composerQueriesFontQuery$data) => void;

    intializeAttributesInfo: (modelAttributes: MPlaza.Attributes, attributes: ComposerAttributeNode[]) => void;
    updateAttribute: (attribute: ComposerAttributeNode) => void;
    cloneAttributes: (attributes: ComposerAttributeNode[]) => ComposerAttributeNode[];
    removeAttributes: (attributeIds: string[]) => void;
    updateOption: (option: ComposerAttributeOptionNode) => void;
    cloneOption: (option: ComposerAttributeOptionNode) => ComposerAttributeOptionNode;
    removeOption: (attributeID: string, optionID: string) => void;
    updateAction: (action: ComposerOptionActionNode) => void;
    cloneAction: (action: ComposerOptionActionNode) => ComposerOptionActionNode;
    removeAction: (attributeID: string, optionID: string, actionID: string) => void;

    updateTemplate: (template: ComposerTemplateNode) => void;
    updateTemplateRestriction: (template: ComposerTemplateNode, sideID: number, key: keyof ComposerTemplateSideRestrictions, value: boolean) => void;
    cloneTemplate: (template: ComposerTemplateNode) => ComposerTemplateNode;
    removeTemplate: (templateID: string) => void;
    updateArea: (area: ComposerAreaNode) => void;
    cloneArea: (area: ComposerAreaNode) => ComposerAreaNode;
    removeArea: (areaID: string) => void;
    initializeTemplateInfo: (templates: ComposerTemplateNode[], areas: ComposerAreaNode[]) => void;

    setEcommerceSettings: (ecommerceSettings: ComposerEcommerceSettings) => void;
    selectAttributes: (attribute: ComposerAttributeNode[] | null) => void;
    selectOptions: (options: ComposerAttributeOptionNode[] | null) => void;
    selectActions: (actions: ComposerOptionActionNode[] | null) => void;
    selectTemplates: (templates: ComposerTemplateNode[] | null) => void;
    selectAreas: (areas: ComposerAreaNode[] | null) => void;

    setProductQuantity: (quantity: MPlaza.QuantityRule | null) => void;
    validateAttributes: () => Promise<ComposerAttributeNode[]>;
    validateOptions: (attribute: ComposerAttributeNode) => Promise<ComposerAttributeOptionNode[]>;
};

const getValidationContext = (state: ComposerStore): ComposerValidationContext => ({
    ecommercePlatform: state.ecommerceSettings?.ecommercePlatform,
    merchantProductOptions: state.merchantProductOptions,
    merchantProductOptionsError: state.merchantProductOptionsError
});

// «IS ONE OF» TIENE PIÙ OPZIONI IN UN VALORE SOLO, separate da `;` (2026-10-15): clonando, ogni id si
// ripunta da sé — cercato intero, «a;b» non si trovava e il clone restava legato alle opzioni di prima.
const remapLinkOptions = (optionID: string, optionIdMap: Map<string, string>) =>
    optionID
        .split(';')
        .map((id) => optionIdMap.get(id) || id)
        .join(';');

const withoutComposerAreas = (state: ComposerStore, removedAreaIDs: Set<string>) => {
    const removedAreas = state.areas.filter((area) => removedAreaIDs.has(area.id));
    if (!removedAreas.length) return {};

    const removedSideIDs = new Set(removedAreas.map((area) => area.sideID));
    const areas = state.areas.filter((area) => !removedAreaIDs.has(area.id));
    const templates = state.templates.flatMap((template) => {
        const design = new MPlaza.Design(JSON.parse(JSON.stringify(template.design)));
        design
            .get('sides')
            .models.slice()
            .forEach((side) => {
                if (removedSideIDs.has(side.get('sideID'))) design.get('sides').remove(side);
            });
        design
            .get('designItems')
            .models.slice()
            .forEach((item) => {
                const itemAreas = item.get('areas');
                const hadAreas = itemAreas.length > 0;
                itemAreas.models.slice().forEach((itemArea) => {
                    if (removedSideIDs.has(itemArea.get('sideID'))) itemAreas.remove(itemArea);
                });
                if (hadAreas && itemAreas.length === 0) design.get('designItems').remove(item);
            });
        const restrictions = new Map(template.restrictions);
        removedSideIDs.forEach((sideID) => restrictions.delete(sideID));
        if (template.templateID <= 0 && design.get('sides').length === 0 && design.get('designItems').length === 0) return [];
        return [{ ...template, design, restrictions }];
    });
    const designs = new Map<string, MPlaza.Design>();
    for (const [key, design] of state.designs) if (key.startsWith('deleted:')) designs.set(key, design);
    for (const template of templates) {
        for (const area of areas) designs.set(`${template.id},${area.id}`, template.design);
    }
    const templateIDs = new Set(templates.map((template) => template.id));
    return {
        areas,
        templates,
        designs,
        selectedAreas: state.selectedAreas?.filter((area) => !removedAreaIDs.has(area.id)) || null,
        selectedTemplates: state.selectedTemplates?.filter((template) => templateIDs.has(template.id)) || null
    };
};

// Create store with the modern create pattern
const useComposerStore = create<ComposerStore>((set, store) => {
    return {
        model: null,
        setModel: (model) => set({ model }),

        sceneMaterialList: [],
        sceneObjectList: [],
        sceneCameraList: [],
        setSceneMaterialList: (materials) => set({ sceneMaterialList: materials }),
        setSceneObjectList: (objects) => set({ sceneObjectList: objects }),
        setSceneCameraList: (cameras) => set({ sceneCameraList: cameras }),

        modelAttributes: null,
        attributes: [],
        attributesSteps: null,
        selectedAttributes: null,
        selectedOptions: null,
        selectedActions: null,
        attributeFace: 'options',
        optionFace: 'actions',
        setAttributeFace: (attributeFace) => set({ attributeFace }),
        setOptionFace: (optionFace) => set({ optionFace }),
        attributesGroups: null,

        templates: [],
        selectedTemplates: null,
        areas: [],
        designs: new Map(),
        setDesigns: (designs: Map<string, MPlaza.Design>) => set({ designs }),
        selectedAreas: null,

        ecommerceSettings: null,
        merchantProductOptions: [],
        merchantProductOptionsError: null,
        printTypes: [],
        extensionFieldDefinitions: [],
        setRequiredEditorData: (data) => set(data),
        fonts: null,
        setFonts: (fonts) => set({ fonts }),
        productQuantity: null,
        intializeAttributesInfo: (modelAttributes, attributes) =>
            set({
                modelAttributes,
                attributes,
                attributesSteps: attributes.filter((attribute) => attribute.type === ComposerAttributeType.AttributeStep),
                attributesGroups: attributes.filter((attribute) => attribute.type === ComposerAttributeType.AttributeGroup),
                selectedAttributes: null,
                selectedOptions: null,
                selectedActions: null,
                attributeFace: 'options',
                optionFace: 'actions'
            }),
        setEcommerceSettings: (ecommerceSettings) => set({ ecommerceSettings }),
        selectAttributes: (attributes) => {
            // Reset selectedOptions only if the attribute selection actually changed
            set((state) => {
                const previousAttributeIds =
                    state.selectedAttributes
                        ?.map((attr) => attr.id)
                        .sort()
                        .join(',') || '';
                const newAttributeIds =
                    attributes
                        ?.map((attr) => attr.id)
                        .sort()
                        .join(',') || '';
                const attributeChanged = previousAttributeIds !== newAttributeIds;

                return {
                    selectedAttributes: attributes,
                    selectedOptions: attributeChanged ? [] : state.selectedOptions,
                    selectedActions: attributeChanged ? null : state.selectedActions,
                    attributeFace: attributeChanged ? 'options' : state.attributeFace
                };
            });
        },
        selectOptions: (options) =>
            set((state) => {
                const previousOptionIds = state.selectedOptions?.map((option) => option.id).join(',') || '';
                const nextOptionIds = options?.map((option) => option.id).join(',') || '';
                const optionChanged = previousOptionIds !== nextOptionIds;
                return {
                    selectedOptions: options,
                    selectedActions: optionChanged ? null : state.selectedActions,
                    optionFace: optionChanged ? 'actions' : state.optionFace
                };
            }),
        selectActions: (actions) => set({ selectedActions: actions }),
        selectTemplates: (templates) => set({ selectedTemplates: templates }),
        selectAreas: (areas) => set({ selectedAreas: areas }),
        //update if present, otherwise add new attribute
        updateAttribute: (attribute) => {
            set((state) => {
                const attr = state.attributes.find((a) => a.id === attribute.id);
                const nextAttributes = attr
                    ? state.attributes.map((candidate) => (candidate.id === attribute.id ? attribute : candidate))
                    : [...state.attributes, attribute];
                const validatedAttributes = validateComposerAttributes(nextAttributes, getValidationContext(state));
                const validatedAttribute = validatedAttributes.find((candidate) => candidate.id === attribute.id) ?? attribute;

                // Update selectedAttributes if this attribute is currently selected
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map(
                        (selectedAttr) => validatedAttributes.find((candidate) => candidate.id === selectedAttr.id) ?? selectedAttr
                    ) ?? null;
                const validatedActions = validatedAttributes.flatMap((candidate) => candidate.options.flatMap((option) => option.actions));
                const updatedSelectedActions =
                    state.selectedActions?.map(
                        (selectedAction) => validatedActions.find((candidate) => candidate.id === selectedAction.id) ?? selectedAction
                    ) ?? null;

                if (attr) {
                    const updatedSelectedOptions =
                        state.selectedOptions?.map(
                            (selectedOption) => validatedAttribute.options.find((option) => option.id === selectedOption.id) ?? selectedOption
                        ) ?? null;
                    return {
                        attributes: validatedAttributes,
                        selectedAttributes: updatedSelectedAttributes,
                        selectedOptions: updatedSelectedOptions,
                        selectedActions: updatedSelectedActions
                    };
                }
                return {
                    attributes: validatedAttributes,
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: state.selectedOptions,
                    selectedActions: updatedSelectedActions
                };
            });
        },
        //append _clone to the id of cloned attributes
        cloneAttributes: (attributes) => {
            let clonedAttributes: ComposerAttributeNode[] = [];
            set((state) => {
                const attributeIdMap = new Map(attributes.map((attribute) => [attribute.id, Zakeke.generateUUID()]));
                const optionIdMap = new Map(attributes.flatMap((attribute) => attribute.options.map((option) => [option.id, Zakeke.generateUUID()] as const)));
                const nextOrderByParent = new Map<string, number>();
                const names = state.attributes.map((attribute) => attribute.name);
                const codes = state.attributes.map((attribute) => attribute.code);

                const nextDisplayOrder = (parentID?: string) => {
                    const key = parentID || '__root__';
                    const current = nextOrderByParent.get(key);
                    if (current != null) {
                        nextOrderByParent.set(key, current + 1);
                        return current;
                    }
                    const next =
                        Math.max(-1, ...state.attributes.filter((attribute) => attribute.parentID === parentID).map((attribute) => attribute.displayOrder)) + 1;
                    nextOrderByParent.set(key, next + 1);
                    return next;
                };

                clonedAttributes = attributes.map((attr) => {
                    const clonedParentID = attr.parentID ? attributeIdMap.get(attr.parentID) || attr.parentID : undefined;
                    const clonedID = attributeIdMap.get(attr.id)!;
                    const cloneAttributeActions = (actions: any[] | undefined) =>
                        (actions ?? []).map((action) => ({ ...action, actionID: null, actionGuid: Zakeke.generateUUID() }));
                    const clonedData = attr.data
                        ? {
                              ...JSON.parse(JSON.stringify(attr.data)),
                              attributeID: -1,
                              attributeGuid: clonedID,
                              preActions: cloneAttributeActions(attr.data.preActions),
                              valueActions: cloneAttributeActions(attr.data.valueActions),
                              postActions: cloneAttributeActions(attr.data.postActions)
                          }
                        : attr.data;
                    const clonedAttr: ComposerAttributeNode = {
                        ...attr,
                        id: clonedID,
                        idNumeric: -1,
                        isNew: true,
                        data: clonedData,
                        parentID: clonedParentID,
                        parentIDNumeric: attr.parentID && attributeIdMap.has(attr.parentID) ? -1 : attr.parentIDNumeric,
                        displayOrder: attr.parentID && attributeIdMap.has(attr.parentID) ? attr.displayOrder : nextDisplayOrder(clonedParentID)
                    };
                    clonedAttr.name = createCopyName(clonedAttr.name, names);
                    names.push(clonedAttr.name);
                    clonedAttr.code = createSecureClonedName(clonedAttr.code, codes);
                    codes.push(clonedAttr.code);
                    clonedAttr.options = attr.options.map((option) => {
                        const clonedOption = {
                            ...option,
                            id: optionIdMap.get(option.id)!,
                            idNumeric: -1,
                            attributeID: clonedAttr.id,
                            isNew: true
                        };
                        clonedOption.code = createSecureClonedName(
                            clonedOption.code,
                            attr.options.map((candidate) => candidate.code)
                        );
                        clonedOption.actions = clonedOption.actions.map((action) => ({
                            ...action,
                            id: Zakeke.generateUUID(),
                            attributeID: clonedAttr.id,
                            optionID: clonedOption.id
                        }));
                        clonedOption.links = clonedOption.links.map((link) => ({
                            ...link,
                            attributeID: attributeIdMap.get(link.attributeID) || link.attributeID,
                            optionID: remapLinkOptions(link.optionID, optionIdMap)
                        }));
                        return clonedOption;
                    });
                    clonedAttr.pricings = attr.pricings.map((pricing) => ({
                        ...pricing,
                        attributePricingID: 0,
                        selectedOptionID: optionIdMap.has(pricing.selectedOptionGuid) ? -1 : pricing.selectedOptionID,
                        selectedOptionGuid: optionIdMap.get(pricing.selectedOptionGuid) || pricing.selectedOptionGuid
                    }));
                    clonedAttr.links = clonedAttr.links.map((link) => ({
                        ...link,
                        attributeID: attributeIdMap.get(link.attributeID) || link.attributeID,
                        optionID: remapLinkOptions(link.optionID, optionIdMap)
                    }));
                    return clonedAttr;
                });
                return { attributes: [...state.attributes, ...clonedAttributes] };
            });
            return clonedAttributes;
        },
        removeAttributes: (attributeIds) =>
            set((state) => {
                const removedAttributeIDs = new Set(attributeIds);
                const removedAreaIDs = new Set(
                    state.areas.filter((area) => area.attributeGuid && removedAttributeIDs.has(area.attributeGuid)).map((area) => area.id)
                );
                const remainingAttributes = state.attributes
                    .filter((attribute) => !removedAttributeIDs.has(attribute.id))
                    .map((attribute) => {
                        const links = attribute.links.filter((link) => !removedAttributeIDs.has(link.attributeID));
                        const options = attribute.options.map((option) => {
                            const optionLinks = option.links.filter((link) => !removedAttributeIDs.has(link.attributeID));
                            return optionLinks.length === option.links.length ? option : { ...option, links: optionLinks };
                        });
                        const optionsChanged = options.some((option, index) => option !== attribute.options[index]);
                        return links.length === attribute.links.length && !optionsChanged ? attribute : { ...attribute, links, options };
                    });
                const validatedAttributes = validateComposerAttributes(remainingAttributes, getValidationContext(state));
                const updatedSelectedAttributes =
                    state.selectedAttributes
                        ?.filter((attribute) => !removedAttributeIDs.has(attribute.id))
                        .map((attribute) => validatedAttributes.find((candidate) => candidate.id === attribute.id) ?? attribute) ?? null;

                return {
                    ...withoutComposerAreas(state, removedAreaIDs),
                    attributes: validatedAttributes,
                    selectedAttributes: updatedSelectedAttributes?.length ? updatedSelectedAttributes : null,
                    selectedOptions: null,
                    selectedActions: null
                };
            }),
        updateOption: (option) =>
            set((state) => {
                const attributeID = option.attributeID;
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);

                if (attributeIndex === -1) {
                    return state;
                }

                const attribute = state.attributes[attributeIndex];
                const updatedAttribute = upsertAndValidateComposerOption(attribute, option, state.attributes, getValidationContext(state));
                const nextAttributes = [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)];
                const validatedAttributes = validateComposerAttributes(nextAttributes, getValidationContext(state));
                const validatedAttribute = validatedAttributes.find((candidate) => candidate.id === updatedAttribute.id) ?? updatedAttribute;
                const updatedOption = validatedAttribute.options.find((candidate) => candidate.id === option.id) ?? option;

                // Update selectedAttributes if this attribute is currently selected
                // This prevents the attribute from being deselected when options change
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === validatedAttribute.id ? validatedAttribute : selectedAttr)) ?? null;

                // Update selectedOptions if the updated option is currently selected
                const updatedSelectedOptions =
                    state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;
                const validatedActions = validatedAttribute.options.flatMap((candidate) => candidate.actions);
                const updatedSelectedActions =
                    state.selectedActions?.map(
                        (selectedAction) => validatedActions.find((candidate) => candidate.id === selectedAction.id) ?? selectedAction
                    ) ?? null;

                return {
                    ...state,
                    attributes: validatedAttributes,
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: updatedSelectedOptions,
                    selectedActions: updatedSelectedActions
                };
            }),
        cloneOption: (option) => {
            let clonedOption: ComposerAttributeOptionNode | null = null;
            set((state) => {
                const attributeID = option.attributeID;
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                const attribute = state.attributes[attributeIndex];
                if (attributeIndex > -1) {
                    clonedOption = {
                        ...option,
                        id: Zakeke.generateUUID(),
                        idNumeric: -1,
                        displayOrder: Math.max(-1, ...attribute.options.map((candidate) => candidate.displayOrder)) + 1,
                        isDefault: false,
                        isNew: true
                    };
                    clonedOption.name = createCopyName(
                        clonedOption.name,
                        attribute.options.map((opt) => opt.name)
                    );
                    clonedOption.code = createSecureClonedName(
                        clonedOption.code,
                        attribute.options.map((opt) => opt.code)
                    );
                    const clonedOptionID = clonedOption.id;
                    clonedOption.actions = clonedOption.actions.map((action) => ({
                        ...action,
                        id: Zakeke.generateUUID(),
                        attributeID,
                        optionID: clonedOptionID
                    }));
                    const updatedOptions = [...state.attributes[attributeIndex].options, clonedOption];
                    const clonedPricings = attribute.pricings
                        .filter((pricing) => pricing.selectedOptionGuid === option.id)
                        .map((pricing) => ({
                            ...pricing,
                            attributePricingID: 0,
                            selectedOptionID: -1,
                            selectedOptionGuid: clonedOptionID
                        }));
                    const updatedAttribute = {
                        ...state.attributes[attributeIndex],
                        options: updatedOptions,
                        pricings: [...attribute.pricings, ...clonedPricings]
                    };

                    // Update selectedAttributes if this attribute is currently selected
                    const updatedSelectedAttributes =
                        state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                    return {
                        attributes: state.attributes.map((attr, index) => (index === attributeIndex ? updatedAttribute : attr)),
                        selectedAttributes: updatedSelectedAttributes
                    };
                }
                return state;
            });
            return clonedOption!;
        },
        removeOption: (attributeID, optionID) =>
            set((state) => {
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                if (attributeIndex === -1) {
                    return state;
                }

                const attribute = state.attributes[attributeIndex];
                const removedOption = attribute.options.find((option) => option.id === optionID);
                const remainingOptions = attribute.options.filter((option) => option.id !== optionID);
                const options =
                    removedOption?.isDefault && remainingOptions.length > 0
                        ? remainingOptions.map((option, index) => ({ ...option, isDefault: index === 0 }))
                        : remainingOptions;

                const updatedAttribute = {
                    ...attribute,
                    options,
                    pricings: attribute.pricings.filter((pricing) => pricing.selectedOptionGuid !== optionID)
                };

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedAttributes:
                        state.selectedAttributes?.map((selected) => (selected.id === updatedAttribute.id ? updatedAttribute : selected)) ?? null,
                    selectedOptions:
                        state.selectedOptions
                            ?.filter((option) => option.id !== optionID)
                            .map((option) => options.find((updatedOption) => updatedOption.id === option.id) ?? option) || null,
                    selectedActions: state.selectedActions?.filter((action) => action.optionID !== optionID) || null
                };
            }),
        //action childs of options
        updateAction: (action) =>
            set((state) => {
                const validatedAction = validateComposerActions([action])[0];
                const optionID = validatedAction.optionID;
                const attributeID = validatedAction.attributeID;

                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                if (attributeIndex === -1) {
                    return state;
                }

                const attribute = state.attributes[attributeIndex];
                const optionIndex = attribute.options.findIndex((opt) => opt.id === optionID);
                if (optionIndex === -1) {
                    return state;
                }

                const option = attribute.options[optionIndex];
                const actionInOption = option.actions.find((a) => a.id === validatedAction.id);

                const updatedOption = {
                    ...option,
                    actions: actionInOption
                        ? option.actions.map((a) => (a.id === validatedAction.id ? validatedAction : a))
                        : [...option.actions, validatedAction]
                };

                const updatedAttribute = {
                    ...attribute,
                    options: [...attribute.options.slice(0, optionIndex), updatedOption, ...attribute.options.slice(optionIndex + 1)]
                };

                // Update selectedAttributes if this attribute is currently selected
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                // Update selectedOptions if this option is currently selected
                const updatedSelectedOptions =
                    state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: updatedSelectedOptions,
                    selectedActions:
                        state.selectedActions?.map((selectedAction) => (selectedAction.id === validatedAction.id ? validatedAction : selectedAction)) ?? null
                };
            }),
        cloneAction: (action) => {
            let clonedAction = null;
            set((state) => {
                const attributeID = action.attributeID;
                const optionID = action.optionID;
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                const optionIndex = attributeIndex > -1 ? state.attributes[attributeIndex].options.findIndex((opt) => opt.id === optionID) : -1;

                if (attributeIndex > -1 && optionIndex > -1) {
                    clonedAction = {
                        ...action,
                        id: Zakeke.generateUUID(),
                        displayOrder:
                            Math.max(-1, ...state.attributes[attributeIndex].options[optionIndex].actions.map((candidate) => candidate.displayOrder)) + 1,
                        isNew: true
                    };
                    const updatedActions = [...state.attributes[attributeIndex].options[optionIndex].actions, clonedAction];
                    const updatedOption = { ...state.attributes[attributeIndex].options[optionIndex], actions: updatedActions };
                    const updatedAttribute = {
                        ...state.attributes[attributeIndex],
                        options: state.attributes[attributeIndex].options.map((opt, index) => (index === optionIndex ? updatedOption : opt))
                    };

                    // Update selectedAttributes if this attribute is currently selected
                    const updatedSelectedAttributes =
                        state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                    // Update selectedOptions if this option is currently selected
                    const updatedSelectedOptions =
                        state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;

                    return {
                        attributes: state.attributes.map((attr, index) => (index === attributeIndex ? updatedAttribute : attr)),
                        selectedAttributes: updatedSelectedAttributes,
                        selectedOptions: updatedSelectedOptions
                    };
                }
                return state;
            });
            return clonedAction!;
        },
        removeAction: (attributeID, optionID, actionID) =>
            set((state) => {
                const attributeIndex = state.attributes.findIndex((attr) => attr.id === attributeID);
                if (attributeIndex === -1) {
                    return state;
                }
                const attribute = state.attributes[attributeIndex];
                const optionIndex = attribute.options.findIndex((opt) => opt.id === optionID);
                if (optionIndex === -1) {
                    return state;
                }

                const option = attribute.options[optionIndex];

                const updatedOption = {
                    ...option,
                    actions: option.actions.filter((a) => a.id !== actionID)
                };

                const updatedAttribute = {
                    ...attribute,
                    options: [...attribute.options.slice(0, optionIndex), updatedOption, ...attribute.options.slice(optionIndex + 1)]
                };

                // Update selectedAttributes if this attribute is currently selected
                const updatedSelectedAttributes =
                    state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ?? null;

                // Update selectedOptions if this option is currently selected
                const updatedSelectedOptions =
                    state.selectedOptions?.map((selectedOpt) => (selectedOpt.id === updatedOption.id ? updatedOption : selectedOpt)) ?? null;

                return {
                    ...state,
                    attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                    selectedAttributes: updatedSelectedAttributes,
                    selectedOptions: updatedSelectedOptions,
                    selectedActions: state.selectedActions?.filter((action) => action.id !== actionID) || null
                };
            }),

        updateTemplate: (template) =>
            set((state) => {
                const index = state.templates.findIndex((t) => t.id === template.id);

                // Update selectedTemplates if this template is currently selected
                const updatedSelectedTemplates =
                    state.selectedTemplates?.map((selectedTemplate) => (selectedTemplate.id === template.id ? template : selectedTemplate)) ?? null;

                if (index > -1) {
                    return {
                        templates: state.templates.map((t) => (t.id === template.id ? template : t)),
                        selectedTemplates: updatedSelectedTemplates
                    };
                }
                return {
                    templates: [...state.templates, template],
                    selectedTemplates: updatedSelectedTemplates
                };
            }),
        updateTemplateRestriction: (template, sideID, key, value) => {
            set((state) => {
                const currentTemplate = state.templates.find((candidate) => candidate.id === template.id);
                if (!currentTemplate) return state;
                const area = state.areas.find((candidate) => candidate.sideID === sideID);
                const areaTemplate = area && state.model ? ComposerTemplateNodeAdapter.withAreaSide(currentTemplate, area, state.model) : currentTemplate;
                const updatedRestrictions = new Map(areaTemplate.restrictions);
                updatedRestrictions.set(sideID, { ...updatedRestrictions.get(sideID), [key]: value });
                const updatedTemplate = { ...areaTemplate, restrictions: updatedRestrictions };
                updatedTemplate.design = ComposerTemplateNodeAdapter.fromTemplateNode(updatedTemplate);
                const designs = new Map(state.designs);
                for (const key of designs.keys()) {
                    if (key.startsWith(`${template.id},`)) designs.set(key, updatedTemplate.design);
                }

                // Update selectedTemplates if this template is currently selected
                const updatedSelectedTemplates =
                    state.selectedTemplates?.map((selectedTemplate) => (selectedTemplate.id === template.id ? updatedTemplate : selectedTemplate)) ?? null;
                return {
                    templates: state.templates.map((t) => (t.id === template.id ? updatedTemplate : t)),
                    selectedTemplates: updatedSelectedTemplates,
                    designs
                };
            });
        },
        getDesign: (templateID, areaID) => {
            const key = [templateID, areaID].join(',');
            return store().designs.get(key) || null;
        },
        updateDesign: (templateID, areaID, design) => {
            set((state) => {
                const key = [templateID, areaID].join(',');
                const updatedDesigns = new Map(state.designs);
                updatedDesigns.set(key, design);
                return {
                    ...state,
                    designs: updatedDesigns
                };
            });
        },
        cloneTemplate: (template) => {
            const clonedDesign = new MPlaza.Design(JSON.parse(JSON.stringify(template.design)));
            clonedDesign.set('designID', 0);
            (clonedDesign as any).set('docID', null);
            (clonedDesign as any).set('isDeleted', false);
            clonedDesign.tempID = Zakeke.generateUUID();
            const clonedTemplate = { ...template, id: Zakeke.generateUUID(), templateID: 0, designDocID: '', design: clonedDesign };
            set((state) => {
                const designs = new Map(state.designs);
                for (const area of state.areas) designs.set([clonedTemplate.id, area.id].join(','), clonedDesign);
                return {
                    ...state,
                    templates: [...state.templates, clonedTemplate],
                    designs
                };
            });
            return clonedTemplate;
        },
        removeTemplate: (templateID) =>
            set((state) => {
                // Remove deleted template from selectedTemplates to prevent stale references
                const updatedSelectedTemplates = state.selectedTemplates?.filter((t) => t.id !== templateID) ?? null;
                const removedTemplate = state.templates.find((template) => template.id === templateID);
                const designs = new Map(state.designs);
                for (const [key, design] of designs) if (removedTemplate && design === removedTemplate.design) designs.delete(key);
                if (removedTemplate?.design.getDesignID()) {
                    (removedTemplate.design as any).set('isDeleted', true);
                    designs.set(`deleted:${removedTemplate.id}`, removedTemplate.design);
                }

                return {
                    ...state,
                    templates: state.templates.filter((t) => t.id !== templateID),
                    selectedTemplates: updatedSelectedTemplates?.length ? updatedSelectedTemplates : null,
                    designs
                };
            }),
        updateArea: (area) => {
            const validatedArea = validateComposerAreas([area], store().sceneObjectList)[0];
            set((state) => {
                const index = state.areas.findIndex((a) => a.id === validatedArea.id);

                // Update selectedAreas if this area is currently selected
                const updatedSelectedAreas =
                    state.selectedAreas?.map((selectedArea) => (selectedArea.id === validatedArea.id ? validatedArea : selectedArea)) ?? null;

                if (index > -1) {
                    return {
                        areas: state.areas.map((a) => (a.id === validatedArea.id ? validatedArea : a)),
                        selectedAreas: updatedSelectedAreas
                    };
                }
                const defaultTemplate = getComposerDefaultTemplate(state.templates, state.model?.get('defaultDesignTemplateID'));
                const templates = defaultTemplate
                    ? state.templates.map((template) =>
                          template.id === defaultTemplate.id ? ComposerTemplateNodeAdapter.withAreaSide(template, validatedArea, state.model!) : template
                      )
                    : [];
                if (!templates.length) {
                    const template = ComposerTemplateNodeAdapter.createNewTemplate('Default Template', state.model!, validatedArea.originalSide!);
                    templates.push(template);
                }
                const designs = new Map(state.designs);
                const areaTemplate = getComposerDefaultTemplate(templates, state.model?.get('defaultDesignTemplateID'));
                if (areaTemplate) {
                    for (const key of designs.keys()) {
                        if (key.startsWith(`${areaTemplate.id},`)) designs.set(key, areaTemplate.design);
                    }
                    designs.set([areaTemplate.id, validatedArea.id].join(','), areaTemplate.design);
                }

                return {
                    areas: [...state.areas, validatedArea],
                    selectedAreas: updatedSelectedAreas,
                    templates,
                    designs
                };
            });
        },
        cloneArea: (area) => {
            const id = Zakeke.generateUUID();
            const sideID = ComposerAttributeNodeAdapter.temporaryNumericID(id);
            const originalSide = new (MPlaza.Side as any)(JSON.parse(JSON.stringify(area.originalSide))) as MPlaza.Side;
            originalSide.set('sideID', sideID);
            (originalSide as any).set('sideGuid', id);
            originalSide.get('areas').forEach((modelArea) => {
                modelArea.set('areaID', -1);
                modelArea.set('guid', MPlaza.generateUUID());
            });
            const originalMeshSides = (area.originalMeshSides ?? []).map((meshSide) => {
                const clone = new MPlaza.ModelSceneMeshSide(JSON.parse(JSON.stringify(meshSide)));
                clone.set('sideID', sideID);
                return clone;
            });
            const clonedArea = validateComposerAreas(
                [
                    {
                        ...area,
                        id,
                        name: createCopyName(
                            area.name,
                            store().areas.map((candidate) => candidate.name)
                        ),
                        sideID,
                        originalSide,
                        originalMeshSides,
                        data: {
                            side: originalSide.toJSON(),
                            meshSides: originalMeshSides.map((meshSide) => meshSide.toJSON())
                        }
                    }
                ],
                store().sceneObjectList
            )[0];
            store().updateArea(clonedArea);
            return clonedArea;
        },
        removeArea: (areaID) => set((state) => withoutComposerAreas(state, new Set([areaID]))),
        initializeTemplateInfo: (templates: ComposerTemplateNode[], areas: ComposerAreaNode[]) => {
            set((state) => {
                // Both server responses and restored local drafts can contain repeated records.
                // Normalize them before they become visible or are used by a later save.
                const uniqueTemplates = Array.from(
                    new Map(templates.map((template) => [template.templateID > 0 ? `saved:${template.templateID}` : `draft:${template.id}`, template])).values()
                );
                const uniqueAreas = Array.from(new Map(areas.map((area) => [area.sideID > 0 ? `saved:${area.sideID}` : `draft:${area.id}`, area])).values());
                const designs = new Map();
                for (const template of uniqueTemplates) {
                    for (const area of uniqueAreas) {
                        const key = [template.id, area.id].join(',');
                        designs.set(key, template.design);
                    }
                }
                return {
                    templates: uniqueTemplates,
                    areas: uniqueAreas,
                    designs,
                    selectedTemplates: null,
                    selectedAreas: null
                };
            });
        },
        validateAttributes: () => {
            const currentState = store();
            return LazyValidateAttributes(currentState.attributes, getValidationContext(currentState)).then((validatedSnapshot) => {
                const latestState = store();
                // Validation yields to the microtask queue. Never replace an edit made in that
                // interval with the older snapshot that started the validation.
                const validatedAttributes =
                    latestState.attributes === currentState.attributes
                        ? validatedSnapshot
                        : validateComposerAttributes(latestState.attributes, getValidationContext(latestState));
                const validatedAreas = validateComposerAreas(latestState.areas, latestState.sceneObjectList);
                set((state) => {
                    // Update selectedAttributes to reflect validated attributes
                    const updatedSelectedAttributes =
                        state.selectedAttributes?.map((selectedAttr) => validatedAttributes.find((attr) => attr.id === selectedAttr.id) ?? selectedAttr) ??
                        null;
                    const validatedOptions = validatedAttributes.flatMap((candidate) => candidate.options);
                    const updatedSelectedOptions =
                        state.selectedOptions?.map(
                            (selectedOption) => validatedOptions.find((candidate) => candidate.id === selectedOption.id) ?? selectedOption
                        ) ?? null;
                    const validatedActions = validatedOptions.flatMap((candidate) => candidate.actions);
                    const updatedSelectedActions =
                        state.selectedActions?.map(
                            (selectedAction) => validatedActions.find((candidate) => candidate.id === selectedAction.id) ?? selectedAction
                        ) ?? null;
                    const updatedSelectedAreas =
                        state.selectedAreas?.map((selectedArea) => validatedAreas.find((area) => area.id === selectedArea.id) ?? selectedArea) ?? null;

                    return {
                        attributes: validatedAttributes,
                        areas: validatedAreas,
                        selectedAttributes: updatedSelectedAttributes,
                        selectedOptions: updatedSelectedOptions,
                        selectedActions: updatedSelectedActions,
                        selectedAreas: updatedSelectedAreas
                    };
                });
                return validatedAttributes;
            });
        },
        validateOptions: (attribute: ComposerAttributeNode) => {
            const currentState = store();
            const currentAttribute = currentState.attributes.find((attr) => attr.id === attribute.id);
            if (!currentAttribute) return Promise.resolve(attribute.options);

            return LazyValidateOptions(currentAttribute, currentAttribute.options, currentState.attributes, getValidationContext(currentState)).then(
                (validatedSnapshot) => {
                    const latestState = store();
                    const latestAttribute = latestState.attributes.find((candidate) => candidate.id === attribute.id);
                    if (!latestAttribute) return [];
                    const validatedOptions =
                        latestState.attributes === currentState.attributes
                            ? validatedSnapshot
                            : validateComposerOptions(latestAttribute, latestAttribute.options, latestState.attributes, getValidationContext(latestState));
                    set((state) => {
                        const attributeIndex = state.attributes.findIndex((attr) => attr.id === attribute.id);
                        if (attributeIndex > -1) {
                            const updatedAttribute = { ...state.attributes[attributeIndex], options: validatedOptions };

                            // Update selectedAttributes if this attribute is currently selected
                            const updatedSelectedAttributes =
                                state.selectedAttributes?.map((selectedAttr) => (selectedAttr.id === updatedAttribute.id ? updatedAttribute : selectedAttr)) ??
                                null;

                            // Update selectedOptions to reflect the validated options
                            const updatedSelectedOptions =
                                state.selectedOptions?.map((selectedOpt) => validatedOptions.find((opt) => opt.id === selectedOpt.id) ?? selectedOpt) ?? null;
                            const validatedActions = validatedOptions.flatMap((candidate) => candidate.actions);
                            const updatedSelectedActions =
                                state.selectedActions?.map(
                                    (selectedAction) => validatedActions.find((candidate) => candidate.id === selectedAction.id) ?? selectedAction
                                ) ?? null;

                            return {
                                attributes: state.attributes.map((attr, index) => (index === attributeIndex ? updatedAttribute : attr)),
                                selectedAttributes: updatedSelectedAttributes,
                                selectedOptions: updatedSelectedOptions,
                                selectedActions: updatedSelectedActions
                            };
                        }
                        return state;
                    });
                    return validatedOptions;
                }
            );
        },
        setProductQuantity: (quantity: MPlaza.QuantityRule | null) => {
            set((state) => ({
                ...state,
                productQuantity: quantity
            }));
        }
    };
});

// Selector helper (optional)
export const useComposerModelAttributes = () => useComposerStore((state) => state.modelAttributes);
export const useComposerAttributes = () => useComposerStore((state) => state.attributes);
export const useComposerAttributesSteps = () => useComposerStore((state) => state.attributesSteps);
export const useComposerAttributesGroups = () => useComposerStore((state) => state.attributesGroups);

export const useComposerEcommerceSettings = () => useComposerStore((state) => state.ecommerceSettings);

export const useSetComposerAttributes = () => useComposerStore((state) => state.intializeAttributesInfo);
export const useUpdateComposerAttributes = () => useComposerStore((state) => state.updateAttribute);
export const useSetComposerEcommerceSettings = () => useComposerStore((state) => state.setEcommerceSettings);

export const useSelectedComposerAttribute = () => useComposerStore((state) => state.selectedAttributes);
export const useSelectedComposerOptions = () => useComposerStore((state) => state.selectedOptions);
export const useSelectedComposerActions = () => useComposerStore((state) => state.selectedActions);

export const useComposerAttributesSelect = () => useComposerStore((state) => state.selectAttributes);
export const useComposerOptionsSelect = () => useComposerStore((state) => state.selectOptions);
export const useComposerActionsSelect = () => useComposerStore((state) => state.selectActions);

export default useComposerStore;
